<template>
  <div class="container mt-4" v-if="marketingAsset">
    <TopNav
      class="mb-4"
      :title="$t('MARKETING.TITLES.MARKETING_ASSETS')"
      :showOpenMainNav="false"
      :icon="['far', 'box-open']"
    >
      <NavItem
        icon="far fa-pencil-alt"
        :text="$t('MARKETING.LABELS.CHANGE_TEMPLATE')"
        @click="marketingAssetTab = 'templates'"
        v-if="marketingAssetTab === 'content'"
      />
    </TopNav>
    <div
      class="row justify-content-between"
      v-if="marketingAssetTab === 'content'"
    >
      <div class="col-12 mb-4">
        <h4>
          {{ $t('MARKETING.TITLES.CUSTOMIZE_ASSET') }}
        </h4>
      </div>
      <div class="col-6">
        <div class="form-group">
          <label>
            {{ $t('MARKETING.LABELS.PRODUCT_OPTIONAL') }}
            <font-awesome-icon
              class="ml-2 c-medium-gray"
              :icon="['far', 'info-circle']"
              id="product-option-info"
            />
            <HMPopover target="product-option-info">
              <span class="p-3 d-block">
                {{ $t('MARKETING.TOOLTIPS.SELECT_A_PRODUCT_TO_TO_GENERATE') }}
              </span>
            </HMPopover>
          </label>
          <v-select
            :options="allTeacherProducts || []"
            label="product_name"
            code="id"
            v-model="product"
            @input="createFromProduct"
          />
        </div>
        <hr class="mb-4" />
        <div class="form-group" v-if="selectedTemplate.hasImage">
          <label>{{ $t('MARKETING.LABELS.IMAGE') }}:</label>
          <HMImageUpload
            :updateLocal="true"
            v-model="marketingAsset.image"
            :cropper="false"
          />
        </div>
        <HMTextInput
          class="w-100"
          :label="$t('MARKETING.LABELS.TITLE') + ':'"
          v-model="marketingAsset.title"
        />
        <div class="form-group">
          <HMTextarea
            class="w-100"
            :label="$t('MARKETING.LABELS.DESCRIPTION') + ':'"
            v-model="marketingAsset.description"
            :showTextLength="false"
          />
        </div>
        <div
          class="form-group d-flex align-items-center mt-1"
          v-if="selectedTemplate.hasPrimaryColor"
        >
          <label>{{ $t('MARKETING.LABELS.ACCENT_COLOR') }}:</label>
          <SimpleColorPicker
            class="ml-3"
            v-model="marketingAsset.primary_color"
          />
        </div>
      </div>
      <div class="col d-flex justify-content-end">
        <div class="d-inline-block">
          <div :class="selectedTemplate.borderClass" class="border">
            <div class="marketing-card" id="marketing-card">
              <component
                :is="selectedTemplate.component"
                :studioUrl="studioUrl"
                :inProgress="isDownload"
                v-model="marketingAsset"
                :editMode="true"
              />
            </div>
          </div>
          <div>
            <HMButton
              class="mt-3 w-100"
              color="outline-gray"
              @click="download"
              :disabled="isDownload"
            >
              <span class="text-center w-100">
                <i class="far fa-file-download text-primary mr-2"></i>
                {{ $t('MARKETING.BUTTON.DOWNLOAD_MARKETING_ASSET') }}
              </span>
            </HMButton>
          </div>
        </div>
      </div>
    </div>
    <div class="row" v-if="marketingAssetTab === 'templates'">
      <div class="col-12 mt-4">
        <h4>
          {{ $t('MARKETING.TITLES.CHOOSE_A_TEMPLATE') }}
        </h4>
        <p>
          {{ $t('MARKETING.TEXT.CLICK_ON_A_DESIGN_BELOW_TO') }}
        </p>
        <h4 class="mt-4 mb-0">
          {{ $t('PRODUCTS.TITLE.SQUARE_CONTENT') }}
          <font-awesome-icon
            class="ml-2 c-medium-gray"
            :icon="['far', 'info-circle']"
            id="square-info"
          />
          <HMPopover target="square-info">
            <span class="p-3 d-block">
              {{
                $t(
                  'PRODUCTS.TEXT.THEY_ARE_BEST_FOR_TIMELINE_POSTS_ON_YOUR_SOCIAL_MEDIA'
                )
              }}
            </span>
          </HMPopover>
        </h4>
      </div>

      <div
        class="col-auto mt-4"
        v-for="item in squareItems"
        :key="item.component"
      >
        <div :class="item.borderClass" class="border">
          <div
            :id="item.id"
            class="marketing-card"
            @click="selectTemplate(item.id)"
          >
            <component
              :editMode="false"
              :is="item.component"
              :studioUrl="studioUrl"
              :inProgress="isDownload"
              v-model="marketingAsset"
            />
          </div>
        </div>
      </div>

      <div class="col-12 mt-5">
        <h4 class="m-0">
          {{ $t('PRODUCTS.TITLE.VERTICAL_CONTENT') }}
          <font-awesome-icon
            class="ml-2 c-medium-gray"
            :icon="['far', 'info-circle']"
            id="vertical-info"
          />
          <HMPopover target="vertical-info">
            <span class="p-3 d-block">
              {{
                $t(
                  'PRODUCTS.TEXT.THEY_ARE_BEST_FOR_STORY_POSTS_ON_YOUR_SOCIAL_MEDIA'
                )
              }}
            </span>
          </HMPopover>
        </h4>
      </div>

      <div
        class="col-auto mt-4"
        v-for="item in verticalItems"
        :key="item.component"
      >
        <div :class="item.borderClass" class="border">
          <div
            :id="item.id"
            class="marketing-card"
            @click="selectTemplate(item.id)"
          >
            <component
              :editMode="false"
              :is="item.component"
              :studioUrl="studioUrl"
              :inProgress="isDownload"
              v-model="marketingAsset"
              @download="download()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import Template1 from '@/web/components/marketing/marketing-assets/Template1.vue';
import Template2 from '@/web/components/marketing/marketing-assets/Template2.vue';
import Template3 from '@/web/components/marketing/marketing-assets/Template3.vue';
import Template4 from '@/web/components/marketing/marketing-assets/Template4.vue';
import Template5 from '@/web/components/marketing/marketing-assets/Template5.vue';
import Template6 from '@/web/components/marketing/marketing-assets/Template6.vue';
import Template7 from '@/web/components/marketing/marketing-assets/Template7.vue';
import Template8 from '@/web/components/marketing/marketing-assets/Template8.vue';
import Template9 from '@/web/components/marketing/marketing-assets/Template9.vue';

import html2canvas from 'html2canvas';
import i18n from '@/web/utils/localization';
import SimpleColorPicker from '@/web/components/marketing/SimpleColorPicker.vue';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';
import quotes from '@/web/utils/quotes';

const marketingAssetsComponents = {
  Template1,
  Template2,
  Template3,
  Template4,
  Template5,
  Template6,
  Template7,
  Template8,
  Template9,
};

const templates = [
  {
    component: 'Template1',
    id: 'card-1',
    borderClass: 'border-white',
    size: 'square',
    hasImage: false,
    hasPrimaryColor: true,
  },
  {
    component: 'Template2',
    id: 'card-2',
    borderClass: 'border-white',
    size: 'square',
    hasImage: true,
    hasPrimaryColor: false,
  },
  {
    component: 'Template3',
    id: 'card-3',
    borderClass: 'border-white',
    size: 'square',
    hasImage: true,
    hasPrimaryColor: false,
  },
  {
    component: 'Template4',
    id: 'card-4',
    borderClass: 'border',
    size: 'square',
    hasImage: false,
    hasPrimaryColor: false,
  },
  {
    component: 'Template5',
    id: 'card-5',
    borderClass: 'border',
    size: 'square',
    hasImage: true,
    hasPrimaryColor: false,
  },
  {
    component: 'Template6',
    id: 'card-6',
    borderClass: 'border-white',
    size: 'square',
    hasImage: true,
    hasPrimaryColor: true,
  },
  {
    component: 'Template7',
    id: 'card-7',
    borderClass: 'border-white',
    size: 'story',
    hasImage: true,
    hasPrimaryColor: false,
  },
  {
    component: 'Template8',
    id: 'card-8',
    borderClass: 'border',
    size: 'story',
    hasImage: true,
    hasPrimaryColor: false,
  },
  {
    component: 'Template9',
    id: 'card-9',
    borderClass: 'border-white',
    size: 'story',
    hasImage: true,
    hasPrimaryColor: true,
  },
];

export default Vue.extend({
  name: 'MarketingAssets',
  components: {
    SimpleColorPicker,
    TopNav,
    NavItem,
    ...marketingAssetsComponents,
  },
  data() {
    return {
      isDownload: false,
      marketingCardDescription: i18n.t('PRODUCTS.TEXT.WRITE_A_CATCHY_PRODUCT'),
      squareItems: templates.filter(item => item.size === 'square'),
      verticalItems: templates.filter(item => item.size === 'story'),
      selectedTemplateId: 'card-1',
      marketingAssetTab: 'templates',
      marketingAsset: null,
      product: null,
    };
  },
  computed: {
    ...mapGetters({
      allTeacherProducts: 'product/allTeacherProducts',
      teacher: 'user/teacher',
      domain: 'setting/domain',
      teacherStudioUrl: 'user/teacherStudioUrl',
    }),
    selectedTemplate() {
      return templates.find(item => item.id === this.selectedTemplateId);
    },
    profilePhoto() {
      return this.teacher?.profile_photo?.thumbnail;
    },
    useCustomDomainUrl: {
      get() {
        return this.$store.getters['setting/useCustomDomainUrl'];
      },
      set(value) {
        this.$store.dispatch('setting/setUseCustomDomainUrl', value);
      },
    },
    hasValidCustomDomain() {
      if (!this.domain) return false;
      if (!this.domain.success) return false;

      return true;
    },
    domainName() {
      return this.domain?.domain;
    },
    studioUrl() {
      if (this.useCustomDomainUrl && this.hasValidCustomDomain) {
        return this.domainName;
      }
      if (process.env.VUE_APP_STUDIO_ON_SUBDOMAINS === 'true') {
        return getStudentViewURL(this.teacherStudioUrl, '', null)?.replace(
          'https://',
          ''
        );
      } else {
        const host = window.location.host;
        return `${host}/${this.teacherStudioUrl}`;
      }
    },
  },
  methods: {
    download() {
      const size = this.selectedTemplate.size;
      const sizes = {
        story: {
          width: 1080,
          height: 1920,
        },
        square: {
          width: 1080,
          height: 1080,
        },
      };

      if (this.isDownload) {
        return;
      }
      this.isDownload = true;
      const el = document.getElementById('marketing-card');
      html2canvas(el, {
        useCORS: true,
        scale: 1,
        windowWidth: sizes[size].width,
        windowHeight: sizes[size].height,
        width: sizes[size].width,
        height: sizes[size].height,
        removeContainer: true,
        onclone: document => {
          const cardEl = document.getElementById('marketing-card');
          cardEl.classList.remove('marketing-card');
          const appEl = document.getElementById('app');
          appEl.innerHTML = '';
          appEl.appendChild(cardEl);
        },
      })
        .then(canvas => {
          const link = document.createElement('a');
          link.download = 'product.png';
          link.href = canvas.toDataURL('image/png');
          link.click();
        })
        .finally(() => {
          this.isDownload = false;
        });
    },
    createFromProduct(product) {
      this.marketingAsset = {
        title: product?.product_name,
        description: this.marketingAsset.description,
        image: product?.cover_file,
        primary_color: this.marketingAsset.primary_color,
      };
      this.product = product;
    },
    selectTemplate(templateId) {
      this.selectedTemplateId = templateId;
      this.marketingAssetTab = 'content';
    },
    getRandomQuote() {
      const randomIndex = Math.floor(Math.random() * quotes.length);
      const [title, quote, author] = quotes[randomIndex];
      return {
        title,
        description: [quote, author].filter(Boolean).join(' - '),
      };
    },
    async loadCustomDomainInfo() {
      try {
        if (!this.hasValidCustomDomain) {
          await this.$store.dispatch('setting/getDomain');
        }
      } catch (_) {
        // it's ok, domain either not allowed or not found
      }
    },
  },
  mounted() {
    this.$store.dispatch('product/getTeacherProducts');
    this.loadCustomDomainInfo();

    this.marketingAsset = {
      ...this.getRandomQuote(),
      image: {
        storage_url: '/assets/images/marketing-assets-default.jpg',
      },
      primary_color: this.teacher?.primary_color,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.marketing-card {
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  zoom: 30%;
  display: inline-block;
}
</style>
